import React from "react";
import { useNavigate } from "react-router-dom";

const HelpPage = () => {
  const navigate = useNavigate();

  const Section = ({ title, children }) => (
    <div className="space-y-3">
      <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
      <div className="text-sm text-gray-600 leading-relaxed space-y-2">
        {children}
      </div>
    </div>
  );

  return (
    <div className="bg-white pt-28 px-4 max-w-4xl mx-auto pb-24">
      <div className="bg-white rounded-3xl shadow p-6 sm:p-8 space-y-10">

        {/* HEADER */}
        <div className="flex justify-between items-center">
          <h1 className="text-2xl sm:text-3xl font-bold">
            Help & Guide
          </h1>
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-gray-500 hover:underline"
          >
            ← Back
          </button>
        </div>

        {/* WHAT IS EXPEDITIO */}
        <Section title="What is Expeditio?">
          <p>
            Expeditio is an <strong>AI-powered travel planning platform</strong> 
            that helps you create, manage and customize trips in seconds.
          </p>
          <p>
            You can generate itineraries using AI and manage everything inside 
            your <strong>Trip Hub</strong> — from plans to bookings.
          </p>
        </Section>

        {/* HOW IT WORKS */}
        <Section title="How does Expeditio work?">
          <ol className="list-decimal ml-5 space-y-1">
            <li>Select trip duration (hours, one-day or multi-day)</li>
            <li>Enter your destination</li>
            <li>Choose your travel type</li>
            <li>Add preferences (optional)</li>
            <li>Click <strong>Generate itinerary</strong></li>
          </ol>
          <p>
            Your itinerary will be created instantly and saved inside your Trip Hub.
          </p>
        </Section>

        {/* TRIP HUB */}
        <Section title="What is Trip Hub?">
          <p>
            Trip Hub is your <strong>central dashboard</strong> where you manage everything related to your trip.
          </p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Itinerary</li>
            <li>Packing list</li>
            <li>Bookings & tickets</li>
            <li>Expenses</li>
            <li>Documents</li>
          </ul>
        </Section>

        {/* EDITING */}
        <Section title="Can I edit my itinerary?">
          <p>
            Yes. You can modify your trip anytime.
          </p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Edit activities</li>
            <li>Reorder plans</li>
            <li>Customize based on your needs</li>
          </ul>
        </Section>

        {/* AI */}
        <Section title="How does the AI generate itineraries?">
          <p>
            The AI creates structured travel plans based on:
          </p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Destination</li>
            <li>Time available</li>
            <li>Travel type</li>
            <li>Realistic pacing</li>
          </ul>
          <p>
            This ensures your trip is practical and easy to follow.
          </p>
        </Section>

        {/* TOKENS */}
        <Section title="What are tokens?">
          <p>
            Tokens are used to generate AI itineraries.
          </p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Each AI generation uses tokens</li>
            <li>New users receive free tokens</li>
            <li>Admins may have unlimited access</li>
          </ul>
        </Section>

        {/* EARN TOKENS */}
        <Section title="How to earn more tokens?">
          <p>You can earn tokens by:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Completing quizzes</li>
            <li>Using in-app activities</li>
          </ul>
        </Section>

        {/* DEMO */}
        <Section title="What is Demo mode?">
          <p>
            Enter <strong>"demo"</strong> as the destination to see a sample itinerary.
          </p>
          <ul className="list-disc ml-5 space-y-1">
            <li>No tokens are used</li>
            <li>Trip is not saved</li>
            <li>Useful for testing the app</li>
          </ul>
        </Section>

        {/* SAVE */}
        <Section title="How are trips saved?">
          <p>
            Trips are automatically saved in your Trip Hub after generation.
          </p>
          <p>
            You can access them anytime from your dashboard.
          </p>
        </Section>

        {/* SHARE */}
        <Section title="How can I share my trip?">
          <p>
            You can generate a share link or QR code to share your itinerary.
          </p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Others can view your trip</li>
            <li>They can copy it to their account</li>
          </ul>
        </Section>

        {/* COMMON ISSUES */}
        <Section title="Common issues & solutions">
          <ul className="list-disc ml-5 space-y-1">
            <li>
              <strong>AI not responding:</strong> Try again after some time.
            </li>
            <li>
              <strong>No tokens left:</strong> Earn more through quizzes.
            </li>
            <li>
              <strong>Changes not saved:</strong> Refresh or check your connection.
            </li>
          </ul>
        </Section>

      </div>
    </div>
  );
};

export default HelpPage;